import { useQuery } from "@tanstack/react-query";
import { getConstructionWeather, getWorksiteWeather } from "@/adapters/api";
import { useActiveCanteiro } from "@/features/canteiros/hooks/use-active-canteiro.ts";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";
import { toWeatherView } from "../utils/map.ts";

const STALE_TIME = 5 * 60_000;

type WeatherTarget = { kind: "construction" | "worksite"; id: string };

export function useWeather() {
	const { obra } = useActiveObra();
	const { canteiro } = useActiveCanteiro();

	let target: WeatherTarget | null = null;
	if (obra) {
		target = { kind: "construction", id: obra.id };
	} else if (canteiro) {
		target = { kind: "worksite", id: canteiro.id };
	}

	const query = useQuery({
		queryKey: ["weather", target?.kind, target?.id],
		enabled: target !== null,
		retry: false,
		staleTime: STALE_TIME,
		queryFn: async () => {
			if (!target) throw new Error("no active weather context");
			const response =
				target.kind === "construction"
					? await getConstructionWeather(target.id)
					: await getWorksiteWeather(target.id);
			return toWeatherView(response.weather);
		},
	});

	const obraName = obra?.name ?? canteiro?.name ?? "";
	const locationLabel =
		obra && canteiro ? `${canteiro.name} · ${obra.name}` : obraName;

	return { query, hasContext: target !== null, obraName, locationLabel };
}
